export type XEduChatProtocolVersion = '1';

// External host-app protocol (iframe postMessage)
// Canonical message types use the XEDU_CHAT/* prefix.
export type ChatMessageType =
  | 'XEDU_CHAT/READY'
  | 'XEDU_CHAT/INITIATE'
  | 'XEDU_CHAT/INIT'
  | 'XEDU_CHAT/ACTION'
  | 'XEDU_CHAT/HELP_HINTS'
  | 'XEDU_CHAT/RESIZE'
  | 'XEDU_CHAT/ERROR';

// Backward-compat: some older hosts used unprefixed types.
type LegacyChatMessageType = 'READY' | 'INITIATE' | 'INIT' | 'ACTION' | 'RESIZE' | 'ERROR';

export interface XEduChatHelpHintsPayload {
  conversationId?: string;
  topic?: any;
  helpHints: string[];
  ts?: number;
}

export interface XEduChatEnvelope<TPayload = any> {
  protocol: 'xedu-chat';
  version: XEduChatProtocolVersion;
  type: ChatMessageType;
  code?: string;
  message?: string;
  payload?: TPayload;
}

export interface XEduChatInitPayload {
  tenantId?: string;
  accessToken?: string;
  // When the host mounts multiple iframe instances, subsequent iframes may include this flag
  // to indicate another iframe already established a session.
  // Default behavior when missing is treated as false.
  sessionExists?: boolean;
  // host may include extra fields; we intentionally ignore them here
  [key: string]: any;
}

export interface HostBridgeOptions {
  allowedHostOrigins?: string[];
  allowedHostOriginRegex?: RegExp;
  onInit: (payload: XEduChatInitPayload, event: MessageEvent) => void | Promise<void>;
  onInitiate?: (payload: any, event: MessageEvent) => void | Promise<void>;
  onError?: (error: Error) => void;
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null;
}

function isEmbedded(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return window.parent !== window;
  } catch {
    return false;
  }
}

function isMobileWebBrowser(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator?.userAgent || '';
    return /android|iphone|ipad|ipod|mobile/i.test(ua);
  } catch {
    return false;
  }
}

function isAllowedOrigin(origin: string, opts: HostBridgeOptions): boolean {
  if (!origin || origin === 'null') return false;

  if (opts.allowedHostOrigins && opts.allowedHostOrigins.length > 0) {
    return opts.allowedHostOrigins.includes(origin);
  }

  if (opts.allowedHostOriginRegex) {
    return opts.allowedHostOriginRegex.test(origin);
  }

  return false;
}

export interface HostBridge {
  isEmbedded: boolean;
  getParentOrigin: () => string | null;
  postReady: () => void;
  postResize: (height: number) => void;
  postAction: (action: any) => void;
  postHelpHints: (payload: XEduChatHelpHintsPayload) => void;
  postError: (message: string, detail?: any) => void;
  startResizeObserver: (el: Element) => () => void;
  destroy: () => void;
}

export function createHostBridge(opts: HostBridgeOptions): HostBridge {
  const embedded = isEmbedded();
  let parentOrigin: string | null = null;
  let destroyed = false;

  function safePost(envelope: XEduChatEnvelope) {
    try {
      if (!embedded) return;
      if (!parentOrigin) return;
      if (destroyed) return;
      window.parent?.postMessage(envelope, parentOrigin);
    } catch {
      // ignore
    }
  }

  const onMessage = async (event: MessageEvent) => {
    try {
      if (!embedded) return;
      if (!isAllowedOrigin(event.origin, opts)) return;
      if (!isRecord(event.data)) return;

      const data = event.data as Record<string, any>;
      if (data.protocol !== 'xedu-chat' || data.version !== '1') return;

      const msgType = data.type as ChatMessageType | LegacyChatMessageType;
      const isInit = msgType === 'XEDU_CHAT/INIT' || msgType === 'INIT';
      const isInitiate = msgType === 'XEDU_CHAT/INITIATE' || msgType === 'INITIATE';

      if (!isInit && !isInitiate) return;

      parentOrigin = event.origin;

      // Prefer payload, but tolerate older host shapes:
      // - { payload: {...} }
      // - { init: {...} } (observed in some host apps)
      // - direct top-level fields
      const payloadRaw = isRecord(data.payload)
        ? data.payload
        : isRecord((data as any).init)
          ? (data as any).init
          : isRecord(data)
            ? data
            : {};

      if (isInit) {
        const payload = payloadRaw as XEduChatInitPayload;
        await opts.onInit(payload, event);
        return;
      }

      if (isInitiate) {
        if (!opts.onInitiate) return;
        await opts.onInitiate(payloadRaw, event);
      }
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Host INIT handling failed');
      opts.onError?.(err);
    }
  };

  if (embedded && typeof window !== 'undefined') {
    window.addEventListener('message', onMessage);
  }

  const startResizeObserver = (el: Element) => {
    if (!embedded) return () => {};
    if (typeof ResizeObserver === 'undefined') return () => {};

    // Do NOT emit resize events for mobile web; these repeated events
    // can cause iframe infinite reload loops when the keyboard slides up.
    if (isMobileWebBrowser()) {
      return () => {};
    }

    let raf = 0;

    const observer = new ResizeObserver(() => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try {
          const docEl = document.documentElement;
          const body = document.body;

          const height = Math.max(
            docEl?.scrollHeight || 0,
            docEl?.offsetHeight || 0,
            body?.scrollHeight || 0,
            body?.offsetHeight || 0
          );

          if (height > 0) {
            safePost({ protocol: 'xedu-chat', version: '1', type: 'XEDU_CHAT/RESIZE', payload: { height } });
          }
        } catch {
          // ignore
        }
      });
    });

    observer.observe(el);

    return () => {
      try {
        if (raf) cancelAnimationFrame(raf);
        observer.disconnect();
      } catch {
        // ignore
      }
    };
  };

  return {
    isEmbedded: embedded,
    getParentOrigin: () => parentOrigin,
    postReady: () => {
      safePost({
        protocol: 'xedu-chat',
        version: '1',
        type: 'XEDU_CHAT/READY',
        payload: {
          ts: Date.now(),
          // Optional capability flags. Host may ignore these.
          supportsResize: true,
          supportsTheme: true,
        },
      });
    },
    postResize: (height: number) => {
      safePost({ protocol: 'xedu-chat', version: '1', type: 'XEDU_CHAT/RESIZE', payload: { height } });
    },
    postAction: (action: any) => {
      safePost({ protocol: 'xedu-chat', version: '1', type: 'XEDU_CHAT/ACTION', payload: action });
    },
    postHelpHints: (payload: XEduChatHelpHintsPayload) => {
      const helpHints = Array.isArray(payload?.helpHints)
        ? payload.helpHints.map((h) => String(h || '').trim()).filter(Boolean)
        : [];
      if (helpHints.length === 0) return;

      safePost({
        protocol: 'xedu-chat',
        version: '1',
        type: 'XEDU_CHAT/HELP_HINTS',
        payload: {
          conversationId: payload.conversationId,
          topic: payload.topic,
          helpHints,
          ts: payload.ts ?? Date.now(),
        },
      });
    },
    postError: (message: string, detail?: any) => {
      safePost({
        protocol: 'xedu-chat',
        version: '1',
        type: 'XEDU_CHAT/ERROR',
        message,
        payload: {
          message,
          detail,
        },
      });
    },
    startResizeObserver,
    destroy: () => {
      destroyed = true;
      try {
        if (embedded && typeof window !== 'undefined') {
          window.removeEventListener('message', onMessage);
        }
      } catch {
        // ignore
      }
    },
  };
}
